import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { api } from '../../services/api';

interface Notification {
  id: number;
  type: string;
  category: string;
  description: string;
  tripCode: string;
  notifyDatetime: string;
  snoozeDuration: number;
  snoozedBy: string | null;
  closedBy: string | null;
  status: string;
}

interface NotificationsState {
  notifications: Notification[];
  loading: boolean;
  error: string | null;
  currentNotification: Notification | null;
  lastFetchTime: string | null;
}

const initialState: NotificationsState = {
  notifications: [],
  loading: false,
  error: null,
  currentNotification: null,
  lastFetchTime: null,
};

// Async thunks
export const fetchActiveNotifications = createAsyncThunk(
  'notifications/fetchActive',
  async (atDatetime: string, { rejectWithValue }) => {
    try {
      const response = await api.getActiveNotifications(atDatetime);
      const typedResponse = response as { status: boolean; message: string; data: Notification[] };
      const notificationsData: Notification[] = typedResponse?.data || [];
      return notificationsData;
    } catch (error: any) {
      console.error('Error fetching active notifications:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch active notifications');
    }
  }
);

export const processActiveNotifications = createAsyncThunk(
  'notifications/processActive',
  async (atDatetime: string, { rejectWithValue }) => {
    try {
      const response = await api.processActiveNotifications(atDatetime);
      const typedResponse = response as { status: boolean; message: string; data: any };
      return typedResponse;
    } catch (error: any) {
      console.error('Error processing active notifications:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to process active notifications');
    }
  }
);

export const snoozeNotification = createAsyncThunk(
  'notifications/snooze',
  async ({ id, atDatetime }: { id: number; atDatetime: string }, { rejectWithValue }) => {
    try {
      const response = await api.snoozeNotification(id, atDatetime);
      const typedResponse = response as { status: boolean; message: string; data: Notification };
      return { id, data: typedResponse.data };
    } catch (error: any) {
      console.error(`Error snoozing notification ${id}:`, error);
      return rejectWithValue(error.response?.data?.message || 'Failed to snooze notification');
    }
  }
);

export const closeNotification = createAsyncThunk(
  'notifications/close',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await api.closeNotification(id);
      const typedResponse = response as { status: boolean; message: string; data: Notification };
      return { id, data: typedResponse.data };
    } catch (error: any) {
      console.error(`Error closing notification ${id}:`, error);
      return rejectWithValue(error.response?.data?.message || 'Failed to close notification');
    }
  }
);

// Slice
const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    setCurrentNotification: (state, action: PayloadAction<Notification | null>) => {
      state.currentNotification = action.payload;
    },
    clearNotifications: (state) => {
      state.notifications = [];
      state.error = null;
      state.currentNotification = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch Active Notifications
    builder.addCase(fetchActiveNotifications.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchActiveNotifications.fulfilled, (state, action) => {
      state.loading = false;
      state.notifications = action.payload;
      state.lastFetchTime = new Date().toISOString();
    });
    builder.addCase(fetchActiveNotifications.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Process Active Notifications
    builder.addCase(processActiveNotifications.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(processActiveNotifications.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(processActiveNotifications.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Snooze Notification
    builder.addCase(snoozeNotification.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(snoozeNotification.fulfilled, (state, action) => {
      state.loading = false;
      const { id } = action.payload;
      // Remove the snoozed notification from the active list
      state.notifications = state.notifications.filter(n => n.id !== id);
      if (state.currentNotification?.id === id) {
        state.currentNotification = null;
      }
    });
    builder.addCase(snoozeNotification.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Close Notification
    builder.addCase(closeNotification.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(closeNotification.fulfilled, (state, action) => {
      state.loading = false;
      const { id } = action.payload;
      // Remove the notification from the list
      state.notifications = state.notifications.filter(n => n.id !== id);
      if (state.currentNotification?.id === id) {
        state.currentNotification = null;
      }
    });
    builder.addCase(closeNotification.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
  },
});

export const { setCurrentNotification, clearNotifications, clearError } = notificationsSlice.actions;
export default notificationsSlice.reducer;